import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCycle } from '../context/CycleContext';
import api from '../utils/api';

/**
 * PaymentCallback
 *
 * Landing page after Paystack redirects the user back from checkout.
 * URL pattern: /payment/callback?reference=xxx&trxref=xxx
 *
 * Flow:
 *   1. Validate reference format and session storage match
 *   2. Read `reference` from the query string (with 30s timeout)
 *   3. Call POST /payments/verify-paystack-payment to activate the CyclePurchase
 *   4. Call startNewCycle(reference) to create the JournalCycle
 *   5. Navigate to /journal/self-assessment on success, or show an error
 *
 * HIGH Priority Fixes:
 *   - Add 30 second timeout to prevent UI hang
 *   - Validate reference format (alphanumeric + dots, 10+ chars)
 *   - Check sessionStorage reference matches URL to prevent manipulation
 */
export default function PaymentCallback() {
    const navigate = useNavigate();
    const { startNewCycle } = useCycle();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'starting' | 'success' | 'error'
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        // Prevent duplicate processing on page refresh (React StrictMode or manual)
        const processed = sessionStorage.getItem('paystack_callback_processed');

        const timeoutId = setTimeout(() => {
            setErrorMsg('Payment verification timed out. Please check your email for confirmation, or contact support.');
            setStatus('error');
        }, 30000);

        const params = new URLSearchParams(window.location.search);
        const reference = params.get('reference') || params.get('trxref');

        if (!reference) {
            clearTimeout(timeoutId);
            setErrorMsg('No payment reference found. Please contact support.');
            setStatus('error');
            return;
        }

        // Validate reference format
        if (!/^[a-z0-9._-]{10,}$/i.test(reference)) {
            clearTimeout(timeoutId);
            setErrorMsg('Invalid payment reference format. Please contact support.');
            setStatus('error');
            return;
        }

        // If this reference was already processed, skip directly to success
        if (processed === reference) {
            clearTimeout(timeoutId);
            setStatus('success');
            navigate('/journal/self-assessment', { replace: true });
            return;
        }

        // Trust Paystack's reference returned in the callback URL
        // (Paystack generates its own reference — we don't generate one on the frontend)

        // Step 1: Verify payment with backend
        api.post('/payments/verify-paystack-payment', { reference })
            .then(() => {
                // Step 2: Create the journal cycle
                setStatus('starting');
                return startNewCycle(reference);
            })
            .then((result) => {
                clearTimeout(timeoutId);
                if (result.success) {
                    // Mark as processed to prevent duplicate on refresh
                    sessionStorage.setItem('paystack_callback_processed', reference);
                    setStatus('success');
                    navigate('/journal/self-assessment', { replace: true });
                } else {
                    setErrorMsg(result.message || 'Could not start cycle. Please contact support.');
                    setStatus('error');
                }
            })
            .catch((err) => {
                clearTimeout(timeoutId);
                const msg = err.response?.data?.message || err.message || 'Payment verification failed.';
                setErrorMsg(msg);
                setStatus('error');
            });

        return () => clearTimeout(timeoutId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                {status === 'verifying' && (
                    <>
                        <div style={styles.spinner} />
                        <p style={styles.message}>Verifying your payment…</p>
                    </>
                )}

                {status === 'starting' && (
                    <>
                        <div style={styles.spinner} />
                        <p style={styles.message}>Payment confirmed! Starting your new cycle…</p>
                    </>
                )}

                {status === 'success' && (
                    <p style={styles.message}>All done! Redirecting…</p>
                )}

                {status === 'error' && (
                    <>
                        <p style={{ ...styles.message, color: '#c0392b' }}>
                            {errorMsg}
                        </p>
                        <button
                            style={styles.button}
                            onClick={() => navigate('/dashboard', { replace: true })}
                        >
                            Back to Dashboard
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f6f9',
        padding: '1rem',
    },
    card: {
        background: '#fff',
        borderRadius: '12px',
        padding: '2.5rem',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    },
    spinner: {
        width: '42px',
        height: '42px',
        border: '4px solid #e0e0e0',
        borderTop: '4px solid #2d6a4f',
        borderRadius: '50%',
        animation: 'spin 0.9s linear infinite',
        margin: '0 auto 1.2rem',
    },
    message: {
        fontSize: '1rem',
        color: '#444',
        marginBottom: '1.2rem',
    },
    button: {
        background: '#2d6a4f',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '0.7rem 1.6rem',
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
};
